import classNames from "classnames";
import { find, isNil, some } from "lodash";
import { Calendar } from "PFTypes";
import React, { memo, useCallback, useMemo } from "react";

import { Day } from "../../context/use_calendar_days";
import { getDaysDifference, isDateBetween } from "../calendar.utils";
import css from "./days.module.scss";

interface NominalDaysProps {
  rowCalendarsData: {
    calendarsUsed: string[];
    calendars: Record<string, Calendar>;
    exceptionDays: { date: string; working: boolean }[];
  };
  days: Day[];
  endDate: string;
}

type CalendarWithEndDate = Calendar & {
  endDate: string;
  startDate: string;
};

const isWorkingDay = (calendar: Calendar, day: Date) =>
  some(calendar.nominal_days, (nominalDay) => nominalDay.wday === day.getUTCDay() && nominalDay.working);
const getWorkingByDateMap = (exceptionDays: { date: string; working: boolean }[]) =>
  exceptionDays.reduce(
    (result, { date, working }) => ({
      ...result,
      [new Date(date).getTime()]: working
    }),
    {} as Record<number, boolean | undefined>
  );

export const NonWorkingDays = memo(({ rowCalendarsData, days, endDate }: NominalDaysProps) => {
  const { calendars, calendarsUsed, exceptionDays } = rowCalendarsData;

  const calendarsWithEndDates: CalendarWithEndDate[] = useMemo(
    () =>
      calendarsUsed
        .sort((date1, date2) => getDaysDifference(new Date(date2), new Date(date1)))
        .map((calKey, i) => ({
          ...calendars[calKey],
          endDate: calendarsUsed[i + 1] || endDate,
          startDate: calKey
        })),
    [calendars, calendarsUsed, endDate]
  );

  const getActiveCalendar = useCallback(
    (day: Date) =>
      find(calendarsWithEndDates, (cal) =>
        isDateBetween(day, new Date(cal.startDate), new Date(cal.endDate))
      ),
    [calendarsWithEndDates]
  );

  const nonWorkingDays = useMemo(() => {
    const daysIndices: number[] = [];
    const exceptionDaysMap = getWorkingByDateMap(exceptionDays);
    days.forEach(({ date }, dayIndex) => {
      const activeCalendar = getActiveCalendar(date);
      const exceptionDayState = exceptionDaysMap[date.getTime()];
      const isWorkingExceptionDay = !isNil(exceptionDayState) && exceptionDayState;
      if (!activeCalendar || isWorkingExceptionDay) {
        return;
      }
      const isNonWorkingExceptionDay = !isNil(exceptionDayState) && !exceptionDayState;
      if (!isWorkingDay(activeCalendar, date) || isNonWorkingExceptionDay) {
        daysIndices.push(dayIndex);
      }
    });
    return daysIndices;
  }, [days, exceptionDays, getActiveCalendar]);

  return (
    <div className={css.nominalDays}>
      {nonWorkingDays.map((dayIndex) => {
        const { date, left, width } = days[dayIndex];
        return (
          <div
            key={`${date.getTime()}-non-working-day`}
            className={classNames(css.day, css.nominalDay)}
            style={{
              width: width,
              left
            }}
          />
        );
      })}
    </div>
  );
});

NonWorkingDays.displayName = "NonWorkingDays";
